<template>
  <div>
    <input
      ref="filesInput"
      type="file"
      class="hidden"
      accept=".png, .jpg, .jpeg, .webp, .heic, .heif"
      @change="handleFilesChange"
    />

    <div class="flex flex-col gap-4">
      <!-- Nếu có ảnh thì hiển thị kèm nút xoá -->
      <div v-if="previewImageURL" class="max-w-[340px] relative group">
        <img :src="previewImageURL" alt="Uploaded" class="rounded border hover:opacity-70 max-h-[150px] mx-auto" />
        <button
          @click="removeImage"
          class="absolute top-1 right-1 bg-red-100 hover:bg-white rounded-lg px-3 py-1 cursor-pointer"
        >
          <i class="fa-solid fa-xmark text-red-500"></i>
        </button>
      </div>

      <div class="flex flex-col gap-4" :class="{ hidden: previewImageURL }">
        <div
          ref="uploadField"
          class="max-w-[340px] dark:bg-stone-800 p-1 border-2 border-dashed border-sky-500 dark:border-sky-700 flex space-x-2 items-center shadow-lg cursor-pointer hover:bg-sky-100 dark:hover:bg-stone-600 transition"
          :class="{
            'cursor-not-allowed bg-gray-300': disabled,
            'bg-sky-100': isDragging,
            'bg-white': !isDragging,
          }"
          @click="triggerFilesInput"
        >
          <img src="@/images/icons/add_image.png" alt="Upload images" class="w-1/3" />
          <div>
            <div class="text-sky-600 text-lg">Upload image</div>
            <div class="italic text-normal">(Select or drop here)</div>
          </div>
        </div>

        <!-- Upload từ URL -->
        <div>
          <div class="flex space-x-2 w-full max-w-[340px]">
            <input
              v-model="imageURL"
              type="url"
              placeholder="Or enter image URL"
              class="input-field flex-grow text-sm py-0"
              :disabled="disabled"
              @keydown.enter="handleUrlUpload"
            />
            <button
              class="btn btn--sm btn--primary min-w-[50px]"
              :disabled="disabled || !imageURL"
              @click="handleUrlUpload"
            >
              <i class="fa-solid fa-upload"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, onMounted, onUnmounted } from 'vue'
  import { useNotify } from '$/composables/useNotify'

  const emit = defineEmits(['uploaded'])
  const modelValue = defineModel<File | File[] | null>()
  const imageURL = ref('')
  const previewImageURL = ref('')

  const props = withDefaults(
    defineProps<{
      disabled?: boolean
    }>(),
    {
      disabled: false,
    }
  )

  const { pushError } = useNotify()
  const filesInput = ref<HTMLInputElement | null>(null)
  const isDragging = ref(false)
  const uploadField = ref<HTMLElement | null>(null)

  onMounted(() => {
    const el = uploadField.value
    if (!el) return

    el.addEventListener('dragover', dragOver)
    el.addEventListener('dragenter', dragEnter)
    el.addEventListener('dragleave', dragLeave)
    el.addEventListener('drop', handleDrop)

    setTimeout(() => {
      if (modelValue.value && !previewImageURL.value) {
        previewImageURL.value = URL.createObjectURL(modelValue.value as File)
      }
    }, 200)
  })

  onUnmounted(() => {
    const el = uploadField.value
    if (!el) return

    el.removeEventListener('dragover', dragOver)
    el.removeEventListener('dragenter', dragEnter)
    el.removeEventListener('dragleave', dragLeave)
    el.removeEventListener('drop', handleDrop)
  })

  function triggerFilesInput() {
    if (props.disabled) return
    filesInput.value?.click()
  }

  function handleFilesChange(event: Event) {
    const input = event.target as HTMLInputElement
    const files = Array.from(input.files || [])
    processFiles(files)
  }

  function dragOver(event: DragEvent) {
    event.preventDefault()
    isDragging.value = true
  }

  function dragEnter(event: DragEvent) {
    event.preventDefault()
    isDragging.value = true
  }

  function dragLeave(event: DragEvent) {
    event.preventDefault()
    isDragging.value = false
  }

  function handleDrop(event: DragEvent) {
    event.preventDefault()
    isDragging.value = false
    if (props.disabled) return
    const files = Array.from(event.dataTransfer?.files || [])
    processFiles(files)
  }

  function handleUrlUpload() {
    if (!imageURL.value) return

    fetch(imageURL.value)
      .then((response) => {
        if (!response.ok) throw new Error('Invalid image URL')
        return response.blob()
      })
      .then((blob) => {
        let extension = blob.type.split('/')[1]

        if (extension === 'octet-stream') {
          const match = imageURL.value.match(/\.\w+$/)
          extension = match ? match[0].slice(1) : 'jpg'
        }

        const uniqueId = Date.now() + '-' + Math.floor(Math.random() * 1000)
        const file = new File([blob], `image-from-url-${uniqueId}.${extension}`, {
          type: blob.type,
        })

        processFiles([file])
        imageURL.value = ''
      })
      .catch((err) => pushError(err.message))
  }

  function processFiles(files: File[]) {
    const imageFiles = files.filter((file) => file.type.startsWith('image/'))

    if (imageFiles.length > 0) {
      if (Array.isArray(modelValue.value)) {
        modelValue.value.push(imageFiles[0])
      } else {
        modelValue.value = imageFiles[0]
      }

      if (filesInput.value) filesInput.value.value = ''
      emit('uploaded')
      previewImageURL.value = URL.createObjectURL(imageFiles[0])
    } else {
      pushError('Only image files are allowed')
    }
  }

  function removeImage() {
    if (Array.isArray(modelValue.value)) {
      modelValue.value.splice(0, 1)
    } else {
      modelValue.value = null
    }
    previewImageURL.value = null
  }
</script>
